import { AlertTriangle, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';

import { MockLuminaire } from '../../../types/analytics';

interface InsightsCardProps {
  slotA: MockLuminaire | null;
  /** Slot B may be a real luminaire (mode=product) or null (synthetic / no peer). */
  slotB: MockLuminaire | null;
  /** Portfolio mean lm/W to anchor "market context" lines. */
  portfolioMeanEfficacy: number;
}

type Insight = {
  kind: 'win' | 'gap' | 'context';
  text: string;
};

// MOCK INSIGHT GENERATOR — competitor-style framing.
// In production this would come from a backend rule engine with access to
// the full cross-manufacturer event corpus.
const buildInsights = (a: MockLuminaire, b: MockLuminaire | null, portfolioMeanEfficacy: number): Insight[] => {
  const out: Insight[] = [];

  const aEfficacy = a.lumenOutput / a.power;
  const marketTopClassEfficacy = portfolioMeanEfficacy * 1.18; // notional "premium-tier competitors" anchor
  const efficacyGap = marketTopClassEfficacy - aEfficacy;

  // 1. Efficacy / lumens-per-watt vs competitive class
  if (efficacyGap > 8) {
    out.push({
      kind: 'gap',
      text: `Premium-tier competitors in the ${a.opticCode} class push ~${marketTopClassEfficacy.toFixed(0)} lm/W. ${a.name} delivers ${aEfficacy.toFixed(0)} lm/W — a ${efficacyGap.toFixed(0)} lm/W gap that hurts compatibility scoring on energy-sensitive projects.`,
    });
  } else if (efficacyGap < -3) {
    out.push({
      kind: 'win',
      text: `${a.name} hits ${aEfficacy.toFixed(0)} lm/W — above the ${marketTopClassEfficacy.toFixed(0)} lm/W flagship benchmark for ${a.opticCode}. This is a defensible technical lead worth marketing in product copy.`,
    });
  } else {
    out.push({
      kind: 'context',
      text: `${a.name} sits at ${aEfficacy.toFixed(0)} lm/W, within ±5% of the ${a.opticCode} class median. Efficacy is competitive but not differentiating — wins must come from optic quality, CRI, or smart-control features.`,
    });
  }

  // 2. CRI competitive context
  if (a.cri < 80) {
    out.push({
      kind: 'gap',
      text: `Class leaders in pedestrian / urban-amenity tenders typically require CRI ≥ 80; ${a.name} ships at CRI ${a.cri}. Competitors are investing R&D budget toward CRI 90+ to win civic projects — consider a higher-CRI variant.`,
    });
  } else if (a.cri >= 90) {
    out.push({
      kind: 'win',
      text: `CRI ${a.cri} is a top-decile value in this class — competitor brands averaging ${(a.cri - 6).toFixed(0)} have to discount aggressively to win the same tenders. Lean into this in your spec sheet.`,
    });
  }

  // 3. Top-3 surface share
  const top3Share =
    a.analytics.top100MatchCount > 0 ? (a.analytics.top3MatchCount / a.analytics.top100MatchCount) * 100 : 0;
  if (top3Share < 5 && a.analytics.top100MatchCount > 50) {
    out.push({
      kind: 'gap',
      text: `${a.name} shows up in top-100 matches ${a.analytics.top100MatchCount.toLocaleString()} times but only breaks into the top-3 ${top3Share.toFixed(1)}% of the time. Competitor flagship products convert ~10–14%. The bottleneck is usually IES-file fit-to-task — worth an R&D review of the optic.`,
    });
  } else if (top3Share >= 10) {
    out.push({
      kind: 'win',
      text: `Top-3 surface rate of ${top3Share.toFixed(1)}% is at the flagship-tier benchmark — only ~12% of products in this class clear that bar. This is your highest-converting catalog asset.`,
    });
  }

  // 4. Lead conversion vs class anchor
  if (a.analytics.leadConversionRatePercent < 8 && a.analytics.matchFrequencyTotal > 0) {
    out.push({
      kind: 'gap',
      text: `Lead conversion sits at ${a.analytics.leadConversionRatePercent.toFixed(1)}%. Across TAMAN, peer luminaires with similar compatibility scores convert at 15–22%. The friction is usually the contact path — competitors that ship inline "request quote" inside the spec page see 2× the inquiries.`,
    });
  }

  // 5. Project coverage breadth
  if (a.analytics.projectTypeCount <= 2) {
    out.push({
      kind: 'context',
      text: `${a.name} only surfaces in ${a.analytics.projectTypeCount} project type${a.analytics.projectTypeCount === 1 ? '' : 's'}. Class-leading competitors typically address 5+ project types from the same optic by offering 2–3 wattage variants — your portfolio may benefit from filling that ladder.`,
    });
  }

  // 6. Pair-wise comparison if Slot B is a real luminaire
  if (b) {
    const bEfficacy = b.lumenOutput / b.power;
    const efficacyDiff = aEfficacy - bEfficacy;
    if (Math.abs(efficacyDiff) > 5) {
      out.push({
        kind: efficacyDiff > 0 ? 'win' : 'gap',
        text: `${a.name} runs ${aEfficacy.toFixed(0)} lm/W vs ${b.name} at ${bEfficacy.toFixed(0)} lm/W (${efficacyDiff > 0 ? '+' : ''}${efficacyDiff.toFixed(0)} lm/W). At equivalent specs, the higher-efficacy product wins energy-sensitive bids — manufacturers that close this gap typically invest in driver efficiency and thermal redesign.`,
      });
    }

    const top3Diff = a.analytics.top3MatchCount - b.analytics.top3MatchCount;
    if (Math.abs(top3Diff) > 100) {
      out.push({
        kind: 'context',
        text: `${top3Diff > 0 ? a.name : b.name} appears in top-3 ${Math.abs(top3Diff).toLocaleString()} more times than its sibling. The delta usually means TAMAN's IES-matching favours one optic distribution over the other for the dominant project pool — useful signal for which product to promote.`,
      });
    }
  }

  // Always at least one line
  if (out.length === 0) {
    out.push({
      kind: 'context',
      text: `${a.name} performs in line with the ${a.opticCode} class median across all measured axes. Differentiation would come from feature additions (smart control, higher CRI) rather than raw photometric tuning.`,
    });
  }

  return out;
};

// All three "kinds" sit inside the Taman green palette.
// Visual differentiation comes from FILL vs OUTLINE treatment, not hue.
const KIND_META: Record<
  Insight['kind'],
  { icon: typeof Sparkles; color: string; label: string; treatment: 'filled' | 'outlined' | 'neutral' }
> = {
  win: { icon: TrendingUp, color: '#B3EC49', label: 'Strength', treatment: 'filled' },
  gap: { icon: TrendingDown, color: '#B3EC49', label: 'Gap', treatment: 'outlined' },
  context: { icon: Sparkles, color: '#B3B3B3', label: 'Context', treatment: 'neutral' },
};

export const InsightsCard = ({ slotA, slotB, portfolioMeanEfficacy }: InsightsCardProps) => {
  if (!slotA) return null;
  const insights = buildInsights(slotA, slotB, portfolioMeanEfficacy);
  const gaps = insights.filter((i) => i.kind === 'gap').length;
  const wins = insights.filter((i) => i.kind === 'win').length;

  return (
    <div className="space-y-4">
      {/* Header strip with summary counters */}
      <div className="rounded-xl border border-greyP4 bg-blackP1/40 p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-green01" />
          <h3 className="text-white font-semibold text-sm">Competitive analysis · {slotA.name}</h3>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5 text-greyP1">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-green01">
              <TrendingUp size={10} className="text-black" />
            </span>
            <span className="font-semibold text-green01">{wins}</span> strengths
          </span>
          <span className="flex items-center gap-1.5 text-greyP1">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded border-2 border-green01">
              <TrendingDown size={10} className="text-green01" />
            </span>
            <span className="font-semibold text-green01">{gaps}</span> gaps
          </span>
          <span className="flex items-center gap-1.5 text-greyP1">
            <AlertTriangle size={12} className="text-greyP3" />
            <span className="text-greyP2 text-[11px] italic">Mock — backend pending</span>
          </span>
        </div>
      </div>

      <ul className="space-y-3">
        {insights.map((ins, i) => {
          const meta = KIND_META[ins.kind];
          const Icon = meta.icon;
          const filled = meta.treatment === 'filled';
          const outlined = meta.treatment === 'outlined';
          return (
            <li
              key={i}
              className="rounded-xl border bg-blackP1/40 p-4 flex gap-3"
              style={{ borderColor: meta.treatment === 'neutral' ? '#313131' : `${meta.color}55` }}
            >
              <div
                className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center border-2 ${
                  filled
                    ? 'bg-green01 border-green01'
                    : outlined
                      ? 'bg-transparent border-green01'
                      : 'bg-transparent border-greyP3'
                }`}
              >
                <Icon size={14} className={filled ? 'text-black' : outlined ? 'text-green01' : 'text-greyP2'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: meta.color }}>
                  {meta.label}
                </div>
                <p className="text-xs text-greyP1 leading-relaxed">{ins.text}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
